// @flow
import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Button,
    Columns,
    Column,
    Flex,
    Text,
    useToast
} from '@locus-taxy/neo-ui';
import { useDispatch } from 'react-redux';
import useSendbirdStateContext from '@sendbird/uikit-react/useSendbirdStateContext';
import sendbirdSelectors from '@sendbird/uikit-react/sendbirdSelectors';
import { useChannelContext } from '@sendbird/uikit-react/Channel/context';
import Input from 'common/components/core/input';
import { ChatActions } from 'common/modules/chat/redux/actions/chat';

import { useTranslation } from 'react-i18next';

import theme from './FooterTheme.scss';

const Footer = ({ lastUnsentMessage, setLastUnsentMessage }) => {
    const { t } = useTranslation('liveView');
    const dispatch = useDispatch();
    const { showToast } = useToast();
    const context = useSendbirdStateContext();
    const sdkInstance = sendbirdSelectors.getSdk(context);
    const currentUserId = sdkInstance?.currentUser?.userId;
    const channelContext = useChannelContext();

    const [value, setValue] = useState(lastUnsentMessage ?? '');
    const [isMessageSending, setIsMessageSending] = useState(false);
    const { currentGroupChannel, initialized } = channelContext;
    const { members, unreadMessageCount } = currentGroupChannel;
    const [user] = members.filter(({ userId }) => userId !== currentUserId);
    const disabled = !user?.isActive;

    const inputRef = useRef();

    const onSubmit = () => {
        const message = value.trim();

        if (!initialized && !message) {
            return;
        }

        const sendMessage = sendbirdSelectors.getSendUserMessage(context);

        const params = {};
        params.message = message;

        sendMessage(currentGroupChannel, params)
            .onPending(pendingMessage => {
                setIsMessageSending(true);
                setValue('');
                return pendingMessage;
            })
            .onSucceeded(() => {
                setIsMessageSending(false);
                inputRef.current?.focus();
            })
            .onFailed(e => {
                showToast({
                    title: t('riderManagerChat.messageNotSent'),
                    content: e.message,
                    palette: 'danger',
                    anchorOrigin: { horizontal: 'left', vertical: 'bottom' }
                });
                setIsMessageSending(false);
            });
    };

    const setInputRef = ref => {
        inputRef.current = ref;
    };

    useEffect(() => {
        setLastUnsentMessage(value);
    }, [value, setLastUnsentMessage]);

    useEffect(() => {
        // Update unread channel count once there is no unread message in the channel
        if (initialized) {
            if (!unreadMessageCount) {
                sdkInstance.groupChannel
                    .getTotalUnreadChannelCount()
                    .then(res => {
                        dispatch(ChatActions.setUnreadChannelCount(res));
                    })
                    .catch(err => {
                        // eslint-disable-next-line no-console
                        console.error(
                            'Chat error: Unread count fetch error',
                            err
                        );
                    });
            }
        }
    }, [initialized, unreadMessageCount, sdkInstance, dispatch]);

    const sendButtonDisabled = value.trim() === '' || disabled;

    return (
        <Box width="100%" padding={2} backgroundColor="white">
            <Columns space="small" alignY="center">
                {disabled ? (
                    initialized && (
                        <Flex alignItems="center" justifyContent="center">
                            <Text color="grey.600" fontSize={2}>
                                {t('riderManagerChat.userRemovedFromTeam')}
                            </Text>
                        </Flex>
                    )
                ) : (
                    <>
                        <Column>
                            <Input
                                width="100%"
                                placeholder={t('riderManagerChat.sendMessage')}
                                type="textarea"
                                textAreaMaxRows={4}
                                value={value}
                                onChange={setValue}
                                noResize
                                onEnter={onSubmit}
                                disabled={isMessageSending}
                                setRef={setInputRef}
                                className={theme.messageTextarea}
                            />
                        </Column>
                        <Button
                            appearance="icon"
                            palette="neutral"
                            icon="lotr_send"
                            size="large"
                            onClick={onSubmit}
                            disabled={sendButtonDisabled}
                        />
                    </>
                )}
            </Columns>
        </Box>
    );
};

export default Footer;
